import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { studentAPI, feePaymentAPI } from '../utils/api';
import transferLogo from '../assets/transfer-logo.png';
import '../styles/profile.css';


const getGenderIconClass = (gender) => {
  if (gender === 'Male') return 'fas fa-mars';
  if (gender === 'Female') return 'fas fa-venus';
  return 'fas fa-user';
};

const displayValue = (value) => value || '-';
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));
const formatDisplayDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN') : '-';
const formatWordDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '-';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  useEffect(() => {
    fetchStudent();
  }, [id]);

  useEffect(() => {
    fetchRecentPayments();
  }, [id]);

  const fetchStudent = async () => {
    try {
      const response = await studentAPI.getById(id);
      setStudent(response.data.data.student);
    } catch (error) {
      console.error('Error fetching student:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response = await feePaymentAPI.getAll({
        studentId: id,
        limit: 5,
        page: 1,
      });
      setRecentPayments(response.data.data.payments || []);
    } catch (error) {
      console.error('Error fetching recent payments:', error);
      setRecentPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handlePrintTransferCertificate = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  if (loading) {
    return (
      <Layout>
        <div className="spinner"></div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout>
        <div className="alert alert-error">Student not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="profile-page">
        <div className="profile-header">
          <h1>{student.name}</h1>
          <div className="profile-actions">
            <button className="btn btn-primary" onClick={() => navigate(`/students/edit/${id}`)}>
              <i className="fas fa-pen"></i> Edit
            </button>
            <button
              className="btn btn-success"
              onClick={() => navigate(`/students/${id}/transfer`)}
              disabled={Boolean(student?.isTransferred)}
            >
              <i className="fas fa-right-left"></i> Transfer
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/students')}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
          </div>
        </div>

        <div className="card">
          {student.isTransferred ? (
            <div className="alert alert-info">
              This student was transferred on {displayValue(student.transferDate)}
              <br/>
              {student.transferRemark ? ` With Remark: ${student.transferRemark}` : ''}.
              <br/>
              <button className="btn btn-success mt-3" onClick={handlePrintTransferCertificate}>
                <i className="fas fa-print"></i> Print Transfer Certificate
              </button>
            </div>
          ) : null}
          <div className="profile-content">
            {/* Personal Information */}
            <div className="profile-section">
              <h3>Personal Information</h3>
              <div className="profile-grid">
                <div className="profile-item">
                  <label>Name:</label>
                  <p>{student.name}</p>
                </div>
                <div className="profile-item">
                  <label>Father's Name:</label>
                  <p>{student.fatherName}</p>
                </div>
                <div className="profile-item">
                  <label>Mother's Name:</label>
                  <p>{student.motherName}</p>
                </div>
                <div className="profile-item">
                  <label>Gender:</label>
                  <p><i className={`${getGenderIconClass(student.gender)} gender-icon gender-icon-${(student.gender || '').toLowerCase()}`}
                        aria-hidden="true"></i> {student.gender}
                    </p>
                </div>
                <div className="profile-item">
                  <label>Class:</label>
                  <p>{student.class}</p>
                </div>
                <div className="profile-item">
                  <label>Date of Birth:</label>
                  <p>{new Date(student.dob).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="profile-item">
                  <label>Date of Admission:</label>
                  <p>{new Date(student.doa).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="profile-item">
                  <label>Caste:</label>
                  <p>{displayValue(student.caste)}</p>
                </div>
                <div className="profile-item">
                  <label>Category:</label>
                  <p>{student.category}</p>
                </div>
                <div className="profile-item">
                  <label>Mobile Number:</label>
                  <p>{student.mobileNumber}</p>
                </div>
                <div className="profile-item">
                  <label>Email:</label>
                  <p>{displayValue(student.email)}</p>
                </div>
                <div className="profile-item" style={{ gridColumn: 'span 2' }}>
                  <label>Address:</label>
                  <p>{student.address}</p>
                </div>
              </div>
            </div>

            {/* ID Information */}
            <div className="profile-section">
              <h3>ID Information</h3>
              <div className="profile-grid">
                <div className="profile-item">
                  <label>Aadhar Number:</label>
                  <p>{displayValue(student.aadharNumber)}</p>
                </div>
                <div className="profile-item">
                  <label>Samagra ID:</label>
                  <p>{displayValue(student.samagra_id)}</p>
                </div>
                <div className="profile-item">
                  <label>Family ID (Samagra):</label>
                  <p>{displayValue(student.familyId)}</p>
                </div>
                <div className="profile-item">
                  <label>PEN:</label>
                  <p>{displayValue(student.pen)}</p>
                </div>
                <div className="profile-item">
                  <label>APAAR ID:</label>
                  <p>{displayValue(student.apaarId)}</p>
                </div>
                <div className="profile-item">
                  <label>Scholar Number:</label>
                  <p>{displayValue(student.scholarNumber)}</p>
                </div>
              </div>
            </div>

            {/* Parental Information */}
            <div className="profile-section">
              <h3>Parental Information</h3>
              <div className="profile-grid">
                <div className="profile-item">
                  <label>Father's Aadhar Number:</label>
                  <p>{displayValue(student.fatherAadharNo)}</p>
                </div>
                <div className="profile-item">
                  <label>Mother's Aadhar Number:</label>
                  <p>{displayValue(student.motherAadharNo)}</p>
                </div>
              </div>
              <div className="profile-grid mt-4">
                <div className="profile-item">
                  <label>Father's Occupation:</label>
                  <p>{displayValue(student.fatherOccupation)}</p>
                </div>
                <div className="profile-item">
                  <label>Mother's Occupation:</label>
                  <p>{displayValue(student.motherOccupation)}</p>
                </div>
              </div>
            </div>

            {/* Banking Information */}
            {(student.accountNumber || student.ifscCode || student.bankName) && (
              <div className="profile-section">
                <h3>Banking Information</h3>
                <div className="profile-grid">
                  {student.accountNumber && (
                    <div className="profile-item">
                      <label>Account Number:</label>
                      <p>{student.accountNumber}</p>
                    </div>
                  )}
                  {student.ifscCode && (
                    <div className="profile-item">
                      <label>IFSC Code:</label>
                      <p>{student.ifscCode}</p>
                    </div>
                  )}
                  {student.bankName && (
                    <div className="profile-item">
                      <label>Bank Name:</label>
                      <p>{student.bankName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Documents */}
            {student.documents && student.documents.length > 0 && (
              <div className="profile-section">
                <h3>Documents</h3>
                <ul>
                  {student.documents.map((doc, idx) => (
                    <li key={idx}>
                      {doc.fileName} - Uploaded on {new Date(doc.uploadedAt).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>
        </div>

        <div className="card profile-secondary-card">
          <div className="profile-secondary-header">
            <h3>Recent Fee Payments <small className="text-muted">(Max 5)</small></h3>
            <p>Latest fee collection entries linked to this student.</p>
          </div>

          {paymentsLoading ? (
            <div className="profile-table-state">Loading recent fee payments...</div>
          ) : recentPayments.length === 0 ? (
            <div className="profile-table-state">No fee payment records found for this student.</div>
          ) : (
            <div className="profile-table-wrapper">
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Class</th>
                    <th>Mobile No</th>
                    <th>Session</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{payment.student_name}</td>
                      <td>{displayValue(payment.class)}</td>
                      <td>{displayValue(payment.mobileNumber)}</td>
                      <td>{displayValue(payment.academic_session_name)}</td>
                      <td>{displayValue(payment.fee_category_name)}</td>
                      <td>{formatCurrency(payment.amount)}</td>
                      <td>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {Boolean(student?.isTransferred) && (
        <div className="transfer-certificate-container">
          <div className="transfer-certificate">
            <div className="transfer-certificate__header">
              <div className="transfer-certificate__govt-note">Recognised by Govt.</div>
              <div className="transfer-certificate__brand">
                <img
                  src={transferLogo}
                  alt="I Am Talent Master International School logo"
                  className="transfer-certificate__logo"
                />
                <div className="transfer-certificate__brand-copy">
                  <h1 className="transfer-certificate__school-name">I Am Talent Master International School</h1>
                  <p className="transfer-certificate__meta-line">Nur. to 8th Class</p>
                  <p className="transfer-certificate__address">
                    Address: Sunder Nagar Extenstion, Sukhliya, Indore, Madhya Pradesh 452010
                  </p>
                  <p className="transfer-certificate__meta-line">Mob.: ____________________</p>
                </div>
              </div>
              <div className="transfer-certificate__title-wrap">
                <h2 className="transfer-certificate__title-band">Transfer Certificate</h2>
                <p className="transfer-certificate__subtitle">(Education Code Part 3 Form 4)</p>
              </div>
            </div>

            <div className="transfer-certificate__body">
              <div className="transfer-certificate__topline">
                <div className="transfer-certificate__topline-field">
                  <span>Scholar No.</span>
                  <strong>{displayValue(student.scholarNumber)}</strong>
                </div>
                <div className="transfer-certificate__topline-field transfer-certificate__topline-field--right">
                  <span>Date</span>
                  <strong>{formatDisplayDate(student.transferDate)}</strong>
                </div>
              </div>

              <div className="transfer-certificate__content">
                <div className="transfer-certificate__field-row">
                  <span>This is to certify that</span>
                  <strong>{displayValue(student.name)}</strong>
                </div>
                <div className="transfer-certificate__field-row">
                  <span>Son/Daughter of Shri</span>
                  <strong>{displayValue(student.fatherName)}</strong>
                </div>
                <div className="transfer-certificate__field-row">
                  <span>Mother's Name Smt.</span>
                  <strong>{displayValue(student.motherName)}</strong>
                </div>
                <div className="transfer-certificate__field-row transfer-certificate__field-row--split">
                  <div className="transfer-certificate__field-inline">
                    <span>Mother Tongue</span>
                    <strong>&nbsp;</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>Caste</span>
                    <strong>{displayValue(student.caste)}</strong>
                  </div>
                </div>
                <div className="transfer-certificate__text-row">
                  Attended I Am Talent Master International School, Indore (M.P.)
                </div>
                <div className="transfer-certificate__field-row transfer-certificate__field-row--split transfer-certificate__field-row--fromto">
                  <div className="transfer-certificate__field-inline">
                    <span>From</span>
                    <strong>{formatDisplayDate(student.doa)}</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>to</span>
                    <strong>{formatDisplayDate(student.transferDate)}</strong>
                  </div>
                </div>
                <div className="transfer-certificate__text-row">
                  and now leaves the school today on having paid all the fees due the school upto.
                </div>
                <div className="transfer-certificate__field-row">
                  <span>His/Her date of birth according to the scholar register No. (in fig.)</span>
                  <strong>{formatDisplayDate(student.dob)}</strong>
                </div>
                <div className="transfer-certificate__field-row">
                  <span>(In Words)</span>
                  <strong>{formatWordDate(student.dob)}</strong>
                </div>
                <div className="transfer-certificate__field-row transfer-certificate__field-row--split">
                  <div className="transfer-certificate__field-inline">
                    <span>His/her SSSMID No. is</span>
                    <strong>{displayValue(student.samagra_id)}</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>Aadhar No. is</span>
                    <strong>{displayValue(student.aadharNumber)}</strong>
                  </div>
                </div>
                <div className="transfer-certificate__text-row">
                  He/She has been vaccinated or is otherwise protected form smallpox.
                </div>
                <div className="transfer-certificate__text-row">
                  The last annual promotion examination passed by his/her is as follow.
                </div>
                <div className="transfer-certificate__field-row transfer-certificate__field-row--triple">
                  <div className="transfer-certificate__field-inline">
                    <span>Class Passed</span>
                    <strong>{displayValue(student.class)}</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>Medium</span>
                    <strong>&nbsp;</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>year</span>
                    <strong>{new Date(student.transferDate).getFullYear()}</strong>
                  </div>
                </div>
                <div className="transfer-certificate__field-row transfer-certificate__field-row--split">
                  <div className="transfer-certificate__field-inline">
                    <span>He/She was admitted in class</span>
                    <strong>{displayValue(student.class)}</strong>
                  </div>
                  <div className="transfer-certificate__field-inline">
                    <span>His/Her conduct was</span>
                    <strong>Satisfactory</strong>
                  </div>
                </div>
                <div className="transfer-certificate__field-row">
                  <span>At the time of leaving the school he/she was studying in class</span>
                  <strong>{displayValue(student.class)}</strong>
                </div>
                <div className="transfer-certificate__field-row">
                  <span>His/her Attendance was</span>
                  <strong>&nbsp;</strong>
                </div>
              </div>

              <div className="transfer-certificate__signatures">
                <div className="transfer-certificate__signature">
                  <span>Principal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentDetail;
